import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Section, SectionHeader } from "@/components/site/Section";
import { supabase } from "@/integrations/supabase/client";
import { amIReviewAdmin } from "@/utils/review.functions";
import { getCourse } from "@/content/courses";
import type { ScheduledSession } from "@/content/schedule";
import { keyOf, useOverrides, useRawSessions, type ScheduleOverride } from "@/hooks/useScheduledSessions";

export const Route = createFileRoute("/_authenticated/admin/schedule")({
  head: () => ({
    meta: [
      { title: "Edit course schedule | Leadership Edge Live" },
      { name: "description", content: "Staff view for editing and removing scheduled sessions." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Edit course schedule" },
      { property: "og:description", content: "Staff view for the course schedule." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AdminSchedulePage,
});

/** Staff edit times in Phoenix time (UTC-7 all year); stored as UTC. */
const PHX_OFFSET = -7;
const shift = (hhmm: string, hours: number) => {
  const [h, m] = hhmm.split(":").map(Number);
  return `${String((((h + hours) % 24) + 24) % 24).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};
const toPhx = (utc?: string) => (utc ? shift(utc.slice(0, 5), PHX_OFFSET) : "");
const toUtc = (phx: string) => shift(phx, -PHX_OFFSET);
const twelve = (hhmm: string) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};

const inputClass =
  "mt-1 w-full rounded-[6px] border border-border bg-background px-3 py-2 text-sm text-ink outline-none focus:border-primary";

function AdminSchedulePage() {
  const checkAdmin = useServerFn(amIReviewAdmin);
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const raw = useRawSessions();
  const { overrides, reload } = useOverrides();
  const [query, setQuery] = useState("");
  const [showRemoved, setShowRemoved] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  useEffect(() => {
    checkAdmin()
      .then((r) => setAllowed(r.admin))
      .catch(() => setAllowed(false));
  }, [checkAdmin]);

  const byKey = useMemo(() => new Map(overrides.map((o) => [o.session_key, o])), [overrides]);
  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return raw.filter((s) => {
      const o = byKey.get(keyOf(s));
      if (o?.hidden && !showRemoved) return false;
      const title = getCourse(s.courseSlug)?.title ?? s.courseSlug;
      return !q || title.toLowerCase().includes(q) || s.startDate.includes(q);
    });
  }, [raw, byKey, query, showRemoved]);

  if (allowed === null) return <Section><p className="text-sm text-muted-foreground">Checking access…</p></Section>;
  if (!allowed) return <Section><p className="text-sm text-muted-foreground">This page is for staff only.</p></Section>;

  async function save(key: string, patch: Partial<ScheduleOverride>) {
    const current = byKey.get(key);
    const { error } = await supabase.from("schedule_overrides").upsert({
      session_key: key,
      hidden: current?.hidden ?? false,
      start_time: current?.start_time ?? null,
      end_time: current?.end_time ?? null,
      meeting_dates: current?.meeting_dates ?? null,
      ...patch,
    });
    if (error) alert(error.message);
    await reload();
  }

  async function restore(key: string) {
    const { error } = await supabase.from("schedule_overrides").delete().eq("session_key", key);
    if (error) alert(error.message);
    await reload();
  }

  return (
    <Section>
      <SectionHeader title="Edit course schedule" intro="Change times or dates, or remove a session from the Schedule page. Times are Phoenix time." />
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by course or date (2026-11)"
          className={`${inputClass} sm:max-w-sm`}
        />
        <label className="flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={showRemoved} onChange={(e) => setShowRemoved(e.target.checked)} />
          Show removed sessions
        </label>
      </div>
      <ul className="space-y-3">
        {list.map((s) => {
          const key = keyOf(s);
          return (
            <SessionRow
              key={key}
              session={s}
              override={byKey.get(key)}
              editing={editing === key}
              onEdit={() => setEditing(editing === key ? null : key)}
              onSave={async (patch) => {
                await save(key, patch);
                setEditing(null);
              }}
              onRemove={() => {
                if (confirm("Remove this session from the schedule?")) void save(key, { hidden: true });
              }}
              onRestore={() => void restore(key)}
            />
          );
        })}
      </ul>
    </Section>
  );
}

function SessionRow({
  session: s,
  override: o,
  editing,
  onEdit,
  onSave,
  onRemove,
  onRestore,
}: {
  session: ScheduledSession;
  override?: ScheduleOverride;
  editing: boolean;
  onEdit: () => void;
  onSave: (patch: Partial<ScheduleOverride>) => Promise<void>;
  onRemove: () => void;
  onRestore: () => void;
}) {
  const start = toPhx(o?.start_time ?? s.startTime);
  const end = toPhx(o?.end_time ?? s.endTime);
  const dates = o?.meeting_dates ?? s.meetingDates ?? [s.startDate];
  const [startIn, setStartIn] = useState(start);
  const [endIn, setEndIn] = useState(end);
  const [datesIn, setDatesIn] = useState(dates.join("\n"));
  const title = getCourse(s.courseSlug)?.title ?? s.courseSlug;

  return (
    <li className={`rounded-2xl border border-border/60 bg-background p-5 ${o?.hidden ? "opacity-60" : ""}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="font-semibold text-ink">{title}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {dates.join(", ")} · {twelve(start)}{end ? ` to ${twelve(end)}` : ""} Phoenix
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {s.tpSessionIds?.length ? "From scheduler" : "From spreadsheet"}
            {o?.hidden ? " · Removed" : o ? " · Edited" : ""}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          {o?.hidden ? (
            <button type="button" onClick={onRestore} className="rounded-[6px] border border-primary px-4 py-2 text-sm font-semibold text-primary">
              Restore
            </button>
          ) : (
            <>
              <button type="button" onClick={onEdit} className="rounded-[6px] border border-primary px-4 py-2 text-sm font-semibold text-primary">
                {editing ? "Cancel" : "Edit"}
              </button>
              <button type="button" onClick={onRemove} className="rounded-[6px] border border-destructive px-4 py-2 text-sm font-semibold text-destructive">
                Delete
              </button>
              {o && (
                <button type="button" onClick={onRestore} className="rounded-[6px] px-3 py-2 text-sm text-muted-foreground underline">
                  Undo edits
                </button>
              )}
            </>
          )}
        </div>
      </div>
      {editing && (
        <form
          className="mt-4 grid gap-4 sm:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            const list = datesIn.split(/[\s,]+/).filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d));
            if (!list.length) return alert("Add at least one date as YYYY-MM-DD.");
            void onSave({
              start_time: startIn ? toUtc(startIn) : null,
              end_time: endIn ? toUtc(endIn) : null,
              meeting_dates: list,
            });
          }}
        >
          <label className="text-sm text-ink">
            Start time (Phoenix)
            <input type="time" value={startIn} onChange={(e) => setStartIn(e.target.value)} className={inputClass} />
          </label>
          <label className="text-sm text-ink">
            End time (Phoenix)
            <input type="time" value={endIn} onChange={(e) => setEndIn(e.target.value)} className={inputClass} />
          </label>
          <label className="text-sm text-ink sm:row-span-2">
            Meeting dates, one per line (YYYY-MM-DD)
            <textarea rows={5} value={datesIn} onChange={(e) => setDatesIn(e.target.value)} className={inputClass} />
          </label>
          <button type="submit" className="self-end rounded-[6px] bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground sm:col-span-2 sm:justify-self-start">
            Save changes
          </button>
        </form>
      )}
    </li>
  );
}
